import { createContext, useContext, useRef, useState } from "react";

// Create a React Context object that will hold "common" state and refs
// for the entire survey flow. This lets deeply nested components access
// and modify shared data without prop-drilling.
const CommonContext = createContext();

/**
 * CommonProvider wraps any part of the app that needs access to the shared
 * state and functions defined below. It centralises survey‑specific data
 * (IDs, questions, results, UI strings, etc.) so all steps/screens can
 * reference the same source of truth.
 */
export const CommonProvider = ({children}) => {

    console.log("------------------CommonContext rendered----------------------");

    // ----- STATE HOOKS -----
    // Identifiers for the current survey session and logged-in user.
    const [surveyId, setSurveyId] =  useState(null);
    const [userId, setUserId] = useState(null);

    // Full set of "memories" shown during response tasks (retrieved from API).
    const [memories, setMemories] = useState([]);

    // Mode flag, e.g. to determine Device/No‑Device (VAS vs non‑VAS) behaviour.
    const [sampleMode, setSampleMode] = useState();


    const [attributes, setAttributes] = useState([[]]);

    // Questions that will be shown as part of explicit measures (post‑IAT).
    const [explicitQuestions, setExplicitQuestions] = useState([]);

    // Collected reaction times for practice IAT rounds.
    const [practiceReaction, setPracticeReaction] = useState([]);

    // Number of VAS (aroma) devices detected, for hardware‑dependent logic.
    const [deviceCount, setDeviceCount] = useState(0);

    // Prompts displayed to the participant (varies per experiment setup).
    const [prompts, setPrompts] = useState([]);

    // Stores IDs for specific questions (probably links to API question set).
    const [questionID, setQuestionID] = useState([]);

    // ----- REF HOOKS -----
    // Refs are used here for mutable data that shouldn't trigger re-renders
    // on change, like counters and arrays accumulating trial results.
    const currentMemoryIndex = useRef(0);       // Index of the memory currently being presented.
    const iatTestReaction = useRef([]);         // Reaction times for actual IAT blocks.
    const explicitResults = useRef([]);         // Stores participant's explicit answers.
    const vasStatus = useRef(" ");              

    // UI copy / instructions text blocks for various screens.
    // These come from the API at survey load time and are used across steps.
    const intermissionText = useRef("");
    const implicitExplicitText = useRef(" ");
    const consumptionText = useRef("");
    const surveyEndText = useRef("");
    const welcomeText = useRef("");
    const nextButtonText = useRef("");
    const kioskTimeoutText = useRef("");
    const inputUserIDText = useRef("");
    const continueButtonText = useRef("");
    const beginButtonText = useRef("");

    // Survey timings
    const intermissionNumber = useRef(0);
    const intermissionTime = useRef(0);

    const homeTest = useRef(false);

    /**
     * Reset certain values back to their initial state when starting a new
     * user. This prevents contaminating a new participant's run
     * with the previous one's memory index or reaction data.
     */
    const resetValues = () => {
        setUserId(null);
        setPracticeReaction([]);
        setMemories([]);
        currentMemoryIndex.current = 0;
        iatTestReaction.current = [];
        explicitResults.current = [];
    };

    // Expose all state values, setters, refs, and helper functions via Provider.
    // Any descendent component can consume these via the `useCommon` hook below.
    return(
        <CommonContext.Provider value={{
            surveyId, setSurveyId, 
            userId, setUserId,
            memories, setMemories, 
            currentMemoryIndex, 
            sampleMode, setSampleMode,
            attributes, setAttributes,
            prompts, setPrompts,
            questionID, setQuestionID,
            explicitQuestions, setExplicitQuestions,
            practiceReaction, setPracticeReaction,
            iatTestReaction,
            explicitResults,
            vasStatus,
            resetValues,
            intermissionText,
            implicitExplicitText,
            consumptionText,
            surveyEndText,
            welcomeText,
            nextButtonText,
            kioskTimeoutText,
            inputUserIDText,
            continueButtonText,
            beginButtonText,
            deviceCount, setDeviceCount,
            intermissionNumber,
            intermissionTime,
            homeTest,
        }}>
            {children}
        </CommonContext.Provider>
    );
};

/**
 * Convenience hook for consuming the CommonContext.
 * Ensures that the hook is only called from within a CommonProvider subtree;
 * if not, throws a descriptive error to catch mis‑use early.
 */
export const useCommon = () => {
    const context = useContext(CommonContext);
    if (!context) {
        throw new Error('useCommon must be used within a CommonProvider');
    }
    return context;
};